import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";

import { useCurrentClient } from "hooks/api/use-clients";
import { typekitKitQueryOptions } from "hooks/api/use-theme";

import type { BrandIdentityFormValues } from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import { INVALID_PROJECT_ID_MESSAGE } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.constants";

/**
 * Applies the drafted Adobe Project ID: it only becomes `adobeFontsProjectId` once Typekit resolves
 * it to a kit. Used by the field's ✓ / Enter and by Save, so a typed-but-unapplied ID is never lost.
 */
export const useAdobeProjectId = ({
  getValues,
  setValue,
  setError,
}: Pick<UseFormReturn<BrandIdentityFormValues>, "getValues" | "setValue" | "setError">) => {
  const client = useCurrentClient();
  const queryClient = useQueryClient();
  const [isApplying, setIsApplying] = useState(false);

  const applyDraft = async (): Promise<boolean> => {
    const draft = getValues("fonts.adobeFontsDraft").trim();

    if (draft === getValues("fonts.adobeFontsProjectId")) {
      return true;
    }

    if (!draft) {
      setValue("fonts.adobeFontsProjectId", "", { shouldDirty: true });

      return true;
    }

    setIsApplying(true);

    try {
      await queryClient.fetchQuery(typekitKitQueryOptions(client.id, draft));
      setValue("fonts.adobeFontsProjectId", draft, { shouldDirty: true });

      return true;
    } catch {
      setError("fonts.adobeFontsDraft", { type: "typekit", message: INVALID_PROJECT_ID_MESSAGE });

      return false;
    } finally {
      setIsApplying(false);
    }
  };

  return { applyDraft, isApplying };
};
